import React, { useState, useEffect } from "react";
import Modal from "../../common-components/Modal";
import PrimaryButton from "../../common-components/Buttons/PrimaryButton";
import TertiaryButton from "../../common-components/Buttons/TertiaryButton";
import Select from "../../common-components/Select";
import useCreateCampaign from "./hooks/useCreateCampaign";
import useUpdateCampaign from "./hooks/useUpdateCampaign";
import { CATEGORY_OPTIONS, STATUS_OPTIONS } from "./constants/campaignOptions";
import { toISTISOString, toLocalInput } from "./utils/dateUtils";
import { useTranslation } from 'react-i18next';
import { FaCloudUploadAlt, FaImage, FaFileVideo, FaFilePdf } from "react-icons/fa";

const CreateCampaignModal = ({
  isOpen = false,
  setIsOpen = () => {},
  getAllCampaigns = () => {},
  isEditable = false,
  currentCampaign = {},
  setCurrentCampaign = () => {},
  setIsEditable = () => {},
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    status: "",
    start_date: "",
    end_date: "",
    file: null,
    thumbnail: null,
    price: "",
    external_link: "",
  });
  const [filePreview, setFilePreview] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [thumbnailError, setThumbnailError] = useState("");

  useEffect(() => {
    if (isEditable && currentCampaign && Object.keys(currentCampaign).length > 0) {
      setForm({
        title: currentCampaign.title || "",
        description: currentCampaign.description || "",
        category: currentCampaign.category || "",
        status: currentCampaign.status || "",
        start_date: toLocalInput(currentCampaign.start_date) || "",
        end_date: toLocalInput(currentCampaign.end_date) || "",
        file: null,
        thumbnail: null,
        price: currentCampaign.price || "",
        external_link: currentCampaign.external_link || "",
      });
      if (currentCampaign.file_url || currentCampaign.presigned_url) {
        setFilePreview(currentCampaign.file_url || currentCampaign.presigned_url);
      }
      if (currentCampaign.thumbnail_url || currentCampaign.thumbnail_presigned_url) {
        setThumbnailPreview(
          currentCampaign.thumbnail_url || currentCampaign.thumbnail_presigned_url
        );
      }
    } else {
      setForm({
        title: "",
        description: "",
        category: "",
        status: "",
        start_date: "",
        end_date: "",
        file: null,
        thumbnail: null,
        price: "",
        external_link: "",
      });
      setFilePreview(null);
      setThumbnailPreview(null);
    }
  }, [isEditable, currentCampaign, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm((prev) => ({
        ...prev,
        file: file,
      }));
      // Create preview
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm((prev) => ({
        ...prev,
        thumbnail: file,
      }));
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const { onCreateCampaign, isCampaignCreating } = useCreateCampaign({
    getAllCampaigns,
    onReset: () => {
      setForm({
        title: "",
        description: "",
        category: "",
        status: "",
        start_date: "",
        end_date: "",
        file: null,
        thumbnail: null,
        price: "",
        external_link: "",
      });
      setFilePreview(null);
      setThumbnailPreview(null);
    },
    onCloseModal: () => {
      setIsOpen(false);
      setIsEditable(false);
      setCurrentCampaign({});
    },
  });

  const { onUpdateCampaign, isCampaignUpdating } = useUpdateCampaign({
    getAllCampaigns,
    onCloseModal: () => {
      setIsOpen(false);
      setIsEditable(false);
      setCurrentCampaign({});
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setThumbnailError("");

    // Validate thumbnail requirement for PDFs in video/document category
    const isVideoOrDocument = form.category === "video" || form.category === "document";
    const isPdf = form.file && form.file.name && form.file.name.toLowerCase().endsWith(".pdf");
    const thumbnailRequired = isVideoOrDocument && isPdf;

    if (thumbnailRequired && !form.thumbnail && !thumbnailPreview) {
      setThumbnailError("Thumbnail is required for PDF files in Video or Document category.");
      return;
    }

    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("description", form.description || "");
    formData.append("category", form.category);
    if (form.status) {
      formData.append("status", form.status);
    }
    if (form.start_date) {
      formData.append("start_date", toISTISOString(form.start_date));
    }
    if (form.end_date) {
      formData.append("end_date", toISTISOString(form.end_date));
    }
    if (form.file) {
      formData.append("file", form.file);
    }
    if (form.thumbnail) {
      formData.append("thumbnail", form.thumbnail);
    }
    if (form.price) {
      formData.append("price", form.price);
    }
    if (form.external_link) {
      formData.append("external_link", form.external_link);
    }

    if (isEditable) {
      // For update, only send changed fields
      const payload = form.file || form.thumbnail ? formData : {};
      if (!form.file && !form.thumbnail) {
        // If no file changes, send JSON
        Object.keys(form).forEach((key) => {
          if (key !== "file" && key !== "thumbnail" && form[key] !== "") {
            payload[key] = form[key];
          }
        });
        if (form.start_date) payload.start_date = toISTISOString(form.start_date);
        if (form.end_date) payload.end_date = toISTISOString(form.end_date);
      }
      await onUpdateCampaign(currentCampaign.id, payload);
    } else {
      await onCreateCampaign(formData);
    }
  };

  const onCloseModal = () => {
    setIsOpen(false);
    setIsEditable(false);
    setCurrentCampaign({});
    setForm({
      title: "",
      description: "",
      category: "",
      status: "",
      start_date: "",
      end_date: "",
      file: null,
      thumbnail: null,
      price: "",
      external_link: "",
    });
    setFilePreview(null);
    setThumbnailPreview(null);
    setThumbnailError("");
  };

  const getFileIcon = () => {
    if (form.category === "video") return <FaFileVideo className="text-red-500" />;
    if (form.category === "document") return <FaFilePdf className="text-blue-500" />;
    return <FaImage className="text-green-500" />;
  };

  const getFileAccept = () => {
    if (form.category === "banner" || form.category === "poster" || form.category === "infographic") {
      return ".png,.jpg,.jpeg";
    }
    if (form.category === "video") {
      return ".mp4,.mov,.mkv";
    }
    if (form.category === "document") {
      return ".pdf,.txt,.doc,.docx";
    }
    return "";
  };

  const isLoading = isCampaignCreating || isCampaignUpdating;

  return (
    <Modal
      showModal={isOpen}
      onClose={onCloseModal}
      modalHeight="h-[90%]"
      modalWidth="w-[80%]"
    >
      <div className="p-5">
        <h2 className="text-xl font-bold text-[#4C6AF7] mb-4">
          {isEditable ? t("Edit Campaign") : t("Add New Campaign")}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-[#5E6066]">
                {t("Title")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={form.title}
                onChange={handleInputChange}
                required
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
                placeholder={t("Enter campaign title")}
              />
            </div>
            <div className="overflow-visible relative z-10">
              <label className="block text-sm font-medium mb-1 text-[#5E6066]">
                {t("Category")} <span className="text-red-500">*</span>
              </label>
              <Select
                placeholder="Select category"
                options={CATEGORY_OPTIONS.filter(opt => opt.value !== '')}
                defaultOption={CATEGORY_OPTIONS.find(opt => opt.value === form.category) || {}}
                onSelect={(option) => setForm(prev => ({ ...prev, category: option.value }))}
                onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                name="category"
                required={true}
                readOnly={false}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-[#5E6066]">
              {t("Description")}
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleInputChange}
              rows={3}
              className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
              placeholder={t("Enter campaign description")}
            />
          </div>

          {isEditable && (
            <div className="overflow-visible relative z-10">
              <label className="block text-sm font-medium mb-1 text-[#5E6066]">
                {t("Status")} <span className="text-red-500">*</span>
              </label>
              <Select
                placeholder="Select status"
                options={STATUS_OPTIONS.filter(opt => opt.value !== '')}
                defaultOption={STATUS_OPTIONS.find(opt => opt.value === form.status) || {}}
                onSelect={(option) => setForm(prev => ({ ...prev, status: option.value }))}
                onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
                name="status"
                required={true}
                readOnly={false}
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-[#5E6066]">
                {t("Price")}
              </label>
              <input
                type="text"
                name="price"
                value={form.price}
                onChange={handleInputChange}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
                placeholder={t("Enter price")}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-[#5E6066]">
                {t("External Link")}
              </label>
              <input
                type="url"
                name="external_link"
                value={form.external_link}
                onChange={handleInputChange}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
                placeholder="https://example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-[#5E6066]">
                {t("Start Date")}
              </label>
              <input
                type="datetime-local"
                name="start_date"
                value={form.start_date}
                onChange={handleInputChange}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-[#5E6066]">
                {t("End Date")}
              </label>
              <input
                type="datetime-local"
                name="end_date"
                value={form.end_date}
                onChange={handleInputChange}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-[#5E6066]">
                {t("Upload File")} <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  id="file-upload"
                  onChange={handleFileChange}
                  accept={getFileAccept()}
                  required={!isEditable}
                  className="hidden"
                />
                <label
                  htmlFor="file-upload"
                  className="flex items-center gap-2 px-4 py-2 bg-[#4C6AF7] text-white rounded-lg cursor-pointer hover:bg-[#3B5AE6] transition"
                >
                  <FaCloudUploadAlt />
                  <span className="text-sm">
                    {form.category === "video"
                      ? t("Upload Video")
                      : form.category === "document"
                      ? t("Upload Document")
                      : t("Upload Image")}
                  </span>
                </label>
                {form.file && (
                  <span className="text-sm text-gray-600">{form.file.name}</span>
                )}
              </div>
              {filePreview && (
                <div className="mt-2">
                  {form.category === "video" || form.category === "document" ? (
                    <p className="text-sm text-gray-600">{t("File selected")}</p>
                  ) : (
                    <img
                      src={filePreview}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg"
                    />
                  )}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {form.category === "banner" || form.category === "poster" || form.category === "infographic"
                  ? t("Allowed: .png, .jpg, .jpeg")
                  : form.category === "video"
                  ? t("Allowed: .mp4, .mov, .mkv")
                  : form.category === "document"
                  ? t("Allowed: .pdf, .txt, .doc, .docx")
                  : ""}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-[#5E6066]">
                {t("Thumbnail")}
                {(form.category === "video" || form.category === "document") && (
                  <span className="text-red-500">*</span>
                )}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  id="thumbnail-upload"
                  onChange={handleThumbnailChange}
                  accept="image/*"
                  required={
                    (form.category === "video" || form.category === "document") &&
                    form.file &&
                    form.file.name &&
                    form.file.name.toLowerCase().endsWith(".pdf")
                  }
                  className="hidden"
                />
                <label
                  htmlFor="thumbnail-upload"
                  className="flex items-center gap-2 px-4 py-2 bg-[#4C6AF7] text-white rounded-lg cursor-pointer hover:bg-[#3B5AE6] transition"
                >
                  <FaImage />
                  <span className="text-sm">{t("Upload Thumbnail")}</span>
                </label>
                {form.thumbnail && (
                  <span className="text-sm text-gray-600">{form.thumbnail.name}</span>
                )}
              </div>
              {thumbnailPreview && (
                <div className="mt-2">
                  <img
                    src={thumbnailPreview}
                    alt="Thumbnail Preview"
                    className="w-32 h-32 object-cover rounded-lg"
                  />
                </div>
              )}
              {thumbnailError && (
                <p className="text-xs text-red-500 mt-1">{thumbnailError}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">{t("Allowed: .png, .jpg, .jpeg")}</p>
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-6">
            <TertiaryButton
              buttonName={t("Cancel")}
              onClick={onCloseModal}
              disabled={isLoading}
            />
            <PrimaryButton
              buttonName={isLoading ? (isEditable ? t("Updating...") : t("Adding...")) : (isEditable ? t("Update Campaign") : t("Add Campaign"))}
              type="submit"
              disabled={isLoading}
            />
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CreateCampaignModal;

